import { z } from 'zod'

import { ReceiptStatus, ZReceiptInformation } from '@/entities/user/@x/contract'

export const ZContractId = z.number()

export const ZPaymentMethod = z.enum(['ACCOUNT', 'CARD'])

export const ZMasterContractStatus = z.enum([
  'REVIEWING', // 계약 진행 중
  'CANCELED', // 계약 전 취소
  'READY', // 입주 대기
  'PAYMENT_CANCELED', // 결제 후 취소
  'ACTIVE', // 거주
  'TERMINATING', // 퇴실
  'CLOSED', // 계약 종료
])

export const ZContractStatus = z.enum([
  'APPROVE_READY', // 승인대기
  'PAY_READY', // 결제대기
  'CONTRACT_COMPLETE', // 결제완료
  'CANCEL', // 취소
  'REFUND', // 결제취소
])

export const ZContractActionType = z.enum([
  'WAITING_APPROVAL',
  'WAITING_CHECKIN',
  'WAITING_CHECKOUT',
  'WAITING_CHECK_FINISH',
])

export const ZRefundStatus = z.enum(['REFUND_READY', 'REFUND_COMPLETE'])

export const ZRefundType = z.enum(['SOFT', 'MIDDLE', 'STRONG'])

export const ZContractListItem = z.object({
  contractId: z.number(),
  contractStatus: ZContractStatus,
  contractStatusDescription: z.string(),
  week: z.number(),
  startDate: z.string(),
  endDate: z.string(),
})

export const ZGuestContractListItem = ZContractListItem.extend({
  finalPayFee: z.number(),
})

export const ZHostContractListItem = ZContractListItem.extend({
  hostPayTime: z.string().nullable(),
  totalUsingFee: z.number(),
  hostPayAmount: z.number().nullable(),
})

export const ZMasterContract = z.object({
  initialContractId: z.number(), // 최초 계약 번호
  contractMasterStatus: ZMasterContractStatus, // 총 계약 상태
  deposit: z.number(), // 보증금
  cid: z.number(), // 채팅방 번호
  startDate: z.string(),
  endDate: z.string(),
  rid: z.number(),
  roomName: z.string(),
  addrLot: z.string(),
  addrStreet: z.string(),
  picMain: z.string().nullable(),
  isCallAvailable: z.boolean(), // 전화하기 가능 여부
})

export const ZHostMasterContract = ZMasterContract.extend({
  guestUid: z.number(),
  guestName: z.string(),
  guestPhoneNumber: z.string(),
  isCheckFinishAvailable: z.boolean(), // 퇴실점검하기 가능 여부
  isDepositDeferAvailable: z.boolean(), // 보증금 반환 보류하기 가능 여부
  isDepositDeferReleaseAvailable: z.boolean(), // 보류 해제하기 가능 여부 (삼삼케어 신청하기 가능 여부)
  samsamCareUrl: z.string().nullable(),
  contractList: z.array(ZHostContractListItem),
  tooltipMessage: z.object({
    checkFinishMessage: z.string().nullable(), // 퇴실 점검하기 버튼 툴팁 메시지
    depositDeferReleaseMessage: z.string().nullable(), // 보류 해제하기 버튼 툴팁 메시지
  }),
  contractMasterStatusDescription: z.string().nullable(), // 총 계약 상태 안내 문구
})

export const ZGuestMasterContract = ZMasterContract.extend({
  contractMasterStatusDescription: z.string(),
  extendContractStartDate: z.string(), // 연장 계약 시작일 (가능 여부와 상관 없음)
  hostUid: z.number(),
  hostName: z.string(),
  hostPhoneNumber: z.string(),
  initialContractRequestTime: z.string(), // 최초 계약 요청 일시
  initialContractApproveTime: z.string().nullable(), // 최초 계약 승인 일시
  initialContractCompleteTime: z.string().nullable(), // 최초 계약 결제 완료 일시
  initialContractCheckinTime: z.string().nullable(), // 최초 계약 입주 일시
  lastContractLeaveConfirmTime: z.string().nullable(), // 마지막 계약 퇴실 일시
  lastContractCheckFinishTime: z.string().nullable(), // 마지막 계약 퇴실 확인 일시
  lastContractClosedTime: z.string().nullable(), // 마지막 계약 종료 일시
  isExtendContractAvailable: z.boolean(), // 계약 연장하기 가능 여부
  isCheckinAvailable: z.boolean(), // 입주하기 가능 여부
  isCheckoutAvailable: z.boolean(), // 퇴실하기 가능 여부
  isContractReRequestAvailable: z.boolean(), // 계약 재요청하기 가능 여부
  isFindAnotherRoomAvailable: z.boolean(), // 다른 방 찾아보기 가능 여부
  contractList: z.array(ZGuestContractListItem),
  tooltipMessage: z.object({
    checkinMessage: z.string().nullable(), // 입주하기 버튼 툴팁 메시지
    checkoutMessage: z.string().nullable(), // 퇴실하기 버튼 툴팁 메시지
    extendMessage: z.string().nullable(), // 계약 연장하기 버튼 툴팁 메시지
  }),
})

export const ZRefundTerm = z.object({
  fromDate: z.string().nullable(),
  toDate: z.string().nullable(),
  fromDateDiffDays: z.number().nullable(),
  toDateDiffDays: z.number().nullable(),
  cancelFeePercent: z.number().nullable(),
})

export const ZContractDetail = z.object({
  contractId: z.number(), // 계약 번호
  contractStatus: ZContractStatus, // 계약 상태
  contractStatusDescription: z.string(), // 계약 상태 안내 문구
  cid: z.number(), // 채팅방 번호
  week: z.number(), // 계약 기간 (주)
  startDate: z.string(), // 계약 시작일
  endDate: z.string(), // 계약 종료일
  isExtend: z.boolean(), // 연장 계약 여부
  usingFee: z.number(), // 임대료
  longTermDiscount: z.number(), // 장기계약 할인
  earlyDiscount: z.number(), //즉시입주 할인
  mgmtFee: z.number(), // 관리비
  cleanFee: z.number(), // 청소비
  refundType: ZRefundType, // 환불 유형
  refundTerms: z.array(ZRefundTerm), // 환불 규정
  cancelFee: z.number().nullable(), // 취소된 임대료
  cancelFeePercent: z.number().nullable(), // 취소 수수료 율
  refundAmount: z.number().nullable(), // 게스트 환불 금액
  contractRequestTime: z.string(), // 계약 요청 일시
  approveTime: z.string().nullable(), // 계약 승인 일시
  payDeadlineTime: z.string().nullable(), // 결제 마감 일시
  contractCompleteTime: z.string().nullable(), // 결제 완료 일시 (계약 완료 일시)
  checkinTime: z.string().nullable(), // 입주 일시
  leaveConfirmTime: z.string().nullable(), // 퇴실 일시
  checkFinishTime: z.string().nullable(), // 퇴실 확인 일시
  cancelTime: z.string().nullable(), // 계약 취소 일시
  closedTime: z.string().nullable(), // 계약 종료 일시 (또는 계약 거절, 환불 완료 일시)
  rid: z.number(), // 방 번호
  roomName: z.string(), // 방 이름
  addrLot: z.string(), // 지번 주소
  addrStreet: z.string(), // 도로명 주소
  picMain: z.string().nullable(), // 대표 이미지
  hostUid: z.number(), // 호스트 유저 번호
  hostName: z.string(), // 호스트 이름
  hostPhoneNumber: z.string(), // 호스트 전화번호
  guestUid: z.number(), // 게스트 유저 번호
  guestName: z.string(), // 게스트 이름
  guestPhoneNumber: z.string(), // 게스트 전화번호
  isRefundAvailable: z.boolean(), // 환불하기 가능 여부
})

export const ZGuestContractDetail = ZContractDetail.extend({
  initialContractId: z.number(), // 최초 계약 번호
  contractFee: z.number(),
  finalContractFee: z.number(),
  realUsingFee: z.number(),
  deposit: z.number(),
  finalPayFee: z.number(),
  isOldRefundTerms: z.boolean(), // 환불 규정 변경 전에 환불된 계약인지 여부
  nowTime: z.string(), // 현재 시간
  payType: z.string().nullable(),
  vaBankName: ZPaymentMethod.nullable(), // 가상계좌
  vaAccountHolder: z.string().nullable(),
  vaAccount: z.string().nullable(),
  calculatedCancelFee: z.number().nullable(),
  calculatedCancelFeePercent: z.number().nullable(),
  calculatedContractFee: z.number().nullable(),
  calculatedRefundAmount: z.number().nullable(),
  transactionId: z.string().nullable(),
  orderNumber: z.string().nullable(),
  isCancelAvailable: z.boolean(),
  isPayAvailable: z.boolean(),
  isContractTaxInfoAvailable: z.boolean(),
  isDepositReturnAccountAvailable: z.boolean(),
  isDepositReturnCardAvailable: z.boolean(),
  isContractRefundAccountAvailable: z.boolean(),
  isContractRefundCardAvailable: z.boolean(),
  hasDepositReturnAccountInfo: z.boolean(),
  hasReceiptInfo: z.boolean(),
  contractGuideUrl: z.string(),
})

export const ZHostContractDetail = ZContractDetail.extend({
  totalUsingFee: z.number(), // 이용 요금
  serviceFee: z.number(), // 계약 수수료 (호스트 서비스 이용료)
  hostPayAmount: z.number(), // 최종 정산 금액
  hostPayAmountBeforeOtherFee: z.number().nullable(), // 정산 대상 금액
  refundAccountBankName: z.string().nullable(), // 환불 계좌 은행
  refundAccount: z.string().nullable(), // 환불 계좌 번호
  hostPayTime: z.string().nullable(), // 호스트 정산 일시
  hostPayDescription: z.string(), // 정산 안내 문구
  isApproveRejectAvailable: z.boolean(), // 계약 승인/거절 가능 여부
})

export const ZDepositReturnAccount = z.object({
  depositReturnBankName: z.string().nullable(),
  depositReturnBankAccount: z.string().nullable(),
  depositReturnBankHolder: z.string().nullable(),
  isDepositReturned: z.boolean(),
  depositReturnedAt: z.string().nullable(), // 보증금 반환 일시 ex. 2026-01-05
  isEditAvailable: z.boolean(),
})

export const ZDepositReturnCard = z.object({
  cardName: z.string(),
  cardNo: z.string(),
  isDepositReturned: z.boolean(),
  depositReturnedAt: z.string().nullable(), // 보증금 반환 일시 ex. 2026-01-05
  cardReceiptUrl: z.string(),
})

export const ZContractReceiptInformation = ZReceiptInformation.merge(
  z.object({
    status: z.nativeEnum(ReceiptStatus).nullable(),
    failedReason: z.string().nullable(),
    billUrl: z.string().nullable(),
    isEditAvailable: z.boolean().nullable(), // 수정 가능 여부
  }),
)

export const ZContractReceiptBillUrl = z.object({
  billUrl: z.string(),
})

export const ZCheckInChecklist = z.object({
  payType: ZPaymentMethod,
  rid: z.number(),
  roomName: z.string(),
  depositReturnAccountInfo: ZDepositReturnAccount.omit({
    isEditAvailable: true,
    isDepositReturned: true,
  }).nullable(),
  receiptInfo: ZReceiptInformation.nullable(),
})

export const ZRefundAccount = z.object({
  refundBankName: z.string().nullable(),
  refundBankAccount: z.string().nullable(),
  refundBankHolder: z.string().nullable(),
  isRefunded: z.boolean(),
  refundedAt: z.string().nullable(), // 환불 일시 ex. 2026-01-05
})

export const ZRefundCard = z.object({
  cardName: z.string(),
  cardNo: z.string(),
  isRefunded: z.boolean(),
  refundedAt: z.string().nullable(), // 환불 일시 ex. 2026-01-05
  cardReceiptUrl: z.string(),
})

export const ZRefundChecklist = z.object({
  payType: ZPaymentMethod,
  finalPayFee: z.number(),
  cardNo: z.string().nullable(),
  cardName: z.string().nullable(),
  calculatedCancelFeePercent: z.number(),
  calculatedCancelFee: z.number(),
  calculatedContractFee: z.number(),
  calculatedRefundAmount: z.number(),
  refundAccountInfo: ZRefundAccount.omit({
    isRefunded: true,
  }),
  receiptInfo: ZReceiptInformation,
})

export const ZHostCalculatedRefundInformation = z.object({
  calculatedCancelFeePercent: z.number(),
  calculatedCancelFee: z.number(),
  refundAccountBankName: z.string().nullable(),
  refundAccount: z.string().nullable(),
  refundAccountHolder: z.string().nullable(),
  refundTerms: z.array(ZRefundTerm),
  refundType: ZRefundType,
})

export const ZHostContractActionsCount = z.object({
  currentTime: z.string(),
  waitingApprovalCount: z.number(),
  waitingCheckinCount: z.number(),
  waitingCheckoutCount: z.number(),
  waitingCheckFinishCount: z.number(),
  waitingApprovalRoomPics: z.array(z.string()),
  waitingCheckinRoomPics: z.array(z.string()),
  waitingCheckoutRoomPics: z.array(z.string()),
  waitingCheckFinishRoomPics: z.array(z.string()),
})

export const ZHostContractStatusCount = z.object({
  reviewingCount: z.number(), // 계약 대기 계약 수
  canceledCount: z.number(), // 취소된 계약 수
  readyCount: z.number(), // 입주 대기 계약 수
  paymentCanceledCount: z.number(), // 환불된 계약 수
  activeCount: z.number(), // 거주 중 계약 수
  terminatingCount: z.number(), // 퇴실 중 계약 수
  closedCount: z.number(), // 종료 계약 수
  reviewingContractRoomPics: z.array(z.string()),
  canceledContractRoomPics: z.array(z.string()),
  readyContractRoomPics: z.array(z.string()),
  paymentCanceledContractRoomPics: z.array(z.string()),
  activeContractRoomPics: z.array(z.string()),
  terminatingContractRoomPics: z.array(z.string()),
  closedContractRoomPics: z.array(z.string()),
  currentTime: z.string(), // 기준 일시
})

const ZContractorInfo = z.object({
  name: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  address: z.string(),
  amount: z.number(),
  method: z.string(),
  paymentDate: z.string(),
})

const ZPaymentDetails = z.object({
  usageFee: z.number(),
  deposit: z.number(),
  serviceFee: z.number(),
  total: z.number(),
})

const ZSupplierInfo = z.object({
  companyName: z.string(),
  ceoName: z.string(),
  businessNumber: z.string(),
  customerService: z.string(),
  address: z.string(),
})

const ZPaymentServiceInfo = z.object({
  companyName: z.string(),
  ceoName: z.string(),
  businessNumber: z.string(),
  customerService: z.string(),
  address: z.string(),
})

export const ZGuestContractBill = z.object({
  contractorInfo: ZContractorInfo,
  paymentDetails: ZPaymentDetails,
  supplierInfo: ZSupplierInfo,
  paymentServiceInfo: ZPaymentServiceInfo.nullable(), // 결제 서비스업체 정보 - 존재하지 않으면 null (법인계좌 입금)
  cardReceiptUrl: z.string().nullable(), // 카드 전표 URL - 카드 결제가 아닌 경우 null
})

export const ZReview = z.object({
  contractId: z.number(),
  score: z.number(),
  review: z.string(),
  rid: z.number(),
  roomName: z.string(),
  roomAddress: z.string(),
  picMain: z.string(),
})

export const ZLastActiveContract = z.object({
  initialContractId: z.number(), // 최초 계약 번호
  lastContractId: z.number(), // 마지막 계약 번호
  startDate: z.string(),
  endDate: z.string(),
  extendContractStartDate: z.string(), // 연장 계약 시작 가능일 (가능 여부와 상관 없음)
  isExtendContractAvailable: z.boolean(), // 연장 계약 가능 여부
})

export const ZSimpleReviewTemplate = z.object({
  id: z.number(),
  code: z.string(),
  content: z.string(),
  isForHost: z.boolean(),
})

export const ZHostSamsamCareLink = z.object({
  samsamCareUrl: z.string(),
})

export type TMasterContractStatus = z.infer<typeof ZMasterContractStatus>
export type TPaymentMethod = z.infer<typeof ZPaymentMethod>
export type TContractStatus = z.infer<typeof ZContractStatus>
export type TContractActionType = z.infer<typeof ZContractActionType>
export type TRefundType = z.infer<typeof ZRefundType>
export type TRefundTerm = z.infer<typeof ZRefundTerm>
export type TRefundStatus = z.infer<typeof ZRefundStatus>

export type THostMasterContract = z.infer<typeof ZHostMasterContract>
export type TContractListItem = z.infer<typeof ZContractListItem>
export type THostContractListItem = z.infer<typeof ZHostContractListItem>
export type TGuestContractDetail = z.infer<typeof ZGuestContractDetail>
export type THostContractDetail = z.infer<typeof ZHostContractDetail>
export type TGuestMasterContract = z.infer<typeof ZGuestMasterContract>

export type TDepositReturnAccount = z.infer<typeof ZDepositReturnAccount>
export type TDepositReturnCard = z.infer<typeof ZDepositReturnCard>

export type TContractReceiptInformation = z.infer<
  typeof ZContractReceiptInformation
>
export type TContractReceiptBillUrl = z.infer<typeof ZContractReceiptBillUrl>
export type TRefundAccount = z.infer<typeof ZRefundAccount>
export type TRefundCard = z.infer<typeof ZRefundCard>
export type TCheckInChecklist = z.infer<typeof ZCheckInChecklist>
export type TRefundChecklist = z.infer<typeof ZRefundChecklist>
export type THostCalculatedRefundInformation = z.infer<
  typeof ZHostCalculatedRefundInformation
>

export type TGuestContractBill = z.infer<typeof ZGuestContractBill>

export type THostContractActionsCount = z.infer<
  typeof ZHostContractActionsCount
>
export type THostContractStatusCount = z.infer<typeof ZHostContractStatusCount>

export type TReview = z.infer<typeof ZReview>

export type TLastActiveContract = z.infer<typeof ZLastActiveContract>

export type TSimpleReviewTemplate = z.infer<typeof ZSimpleReviewTemplate>

export type THostSamsamCareLink = z.infer<typeof ZHostSamsamCareLink>
